import { TypedUseSelectorHook, useDispatch as useAppDispatch, useSelector as useOriginSeletor } from 'react-redux';
import type { RootState, AppDispatch } from '../store/store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm as AntdUseForm } from 'antd/lib/form/Form';

export const useDispatch = () => useAppDispatch<AppDispatch>();
export const useSelector: TypedUseSelectorHook<RootState> = useOriginSeletor;


/**
 * T List 类型
 * P 后端请求参数类型 默认注入了pageNum pageSize
 */
export const usePageState = <T, P>(defaultParams = {}) => {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<T[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [params, setParams] = useState<P>({
    pageNum: 1,
    pageSize: 10,
    ...defaultParams
  } as unknown as P);

  function loadTableWithPage (fn: (p: P) => Promise<CustomSuccessData<BackendDataWithPage<T>>>) {
    const returnFn = (otherParams?: P) => {
      return new Promise(async (resolve, reject) => {
        try {
          setLoading(true);
          const { data } = await fn(otherParams || params);
          setList(data.dataList);
          setTotal(data.totalCount);
          setTotalCount(data.totalCount);
          resolve(data.dataList);
        } catch (error) {
          reject(new Error('获取接口数据失败'));
        } finally {
          setLoading(false);
        }
      });
    };
    return returnFn;
  }

  function loadTableWithoutPage (fn: (p: P) => Promise<CustomSuccessData<T[]>>) {
    const returnFn = (otherParams?: P) => {
      return new Promise(async (resolve, reject) => {
        try {
          setLoading(true);
          const { data } = await fn(otherParams || params);
          setList(data);
          resolve(data);
        } catch (error) {
          reject(new Error('获取接口数据失败'));
        } finally {
          setLoading(false);
        }
      });
    };
    return returnFn;
  }

  async function loadList (fn: (p: P) => Promise<CustomSuccessData<BackendDataWithPage<T>>>, par: P) {
    try {
      setLoading(true);
      const { data } = await fn(par);
      setList(data.dataList);
      setTotal(data.totalCount);
      setTotalCount(data.totalCount);
      return data.dataList;
    } catch (error) {
      return new Error('获取接口数据失败');
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    setLoading,
    list,
    setList,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    totalCount,
    setTotalCount,
    loadTableWithPage,
    loadTableWithoutPage,
    params,
    setParams,
    loadList
  };
};

export type PageState<T, P> = ReturnType<typeof usePageState<T, P>>

/**
 * useRouter
 */

export const useRouter = () => {
  const location = useLocation();
  const navigator = useNavigate();
  const to = (path: string, queryParams: Record<string, unknown> = {}, options?: NavigateOptions | undefined) => {
    const queryStrings = Object.entries(queryParams).reduce((a, [key, value]) => {
      return a + key + '=' + value + '&';
    }, '?');
    navigator(path + queryStrings.slice(0, queryStrings.length - 1), options);
  };
  const getSearchParams = () => {
    if (location.search === '') return {};
    const arr = location.search.split('?')[1].split('#')[0].split('&');
    const resObj: Record<string, string> = {};
    arr.forEach(item => {
      let [key, value = ''] = item.split('=');
      resObj[key] = value;
    });
    return resObj;
  };
  const encodeParams = (s: string) => {
    return window.btoa(encodeURI(s));
  };
  const decodeParams = (s: string) => {
    return decodeURI(window.atob(s));
  };
  const getEncodeParams = () => {
    const res = useParams();
    const o: Record<string, string | undefined> = {};
    for (let key in res) {
      const v = decodeParams(res[key] as string);
      if (v.includes('+')) {
        o[key] = v.split('+')[0];
      } else {
        o[key] = res[key];
      }
    }
    return o;
  };
  const getZnInParams = (s?: string) => {
    return decodeParams(s as string).split('+')[1];
  };
  return {
    to,
    getSearchParams,
    getParams: useParams,
    encodeParams,
    getEncodeParams,
    decodeParams,
    getZnInParams,
    back: () => {
      navigator(-1);
    }
  };
};

export const useForm = AntdUseForm;

/**
 *
 * @param effect
 * @param deps
 * @decription 与 useEffect效果一样，忽略首次执行
 */
export const useUpdateEffect = (effect: React.EffectCallback, deps?: React.DependencyList) => {
  const isMounted = useRef(false);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      return effect();
    }
  }, deps);
};

export const useUserInfo = () => {
  const userState = useSelector(s => s.user);
  // if (Object.keys(userState).length > 0) return userState;
  return JSON.parse(localStorage.getItem('userInfo') || '{}') as typeof userState;
};

export const useUserType = () => {
  const userState = useUserInfo();
  return useMemo(() => {
    return {
      userType: userState.userType,
      isEduUser: userState.userType === 'EDU'
    };
  }, [userState]);
};

